import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SHIPMENT_STAGES } from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { useDetailSidebar } from '#/shared/ui/detail-sidebar'
import { prefetchMapboxMap } from '#/shared/ui/mapbox-map'
import { Skeleton } from '#/shared/ui/skeleton'
import {
  NotesSection,
  RouteSection,
  VehicleSection,
  locationToFormDefaults,
  shipmentToFormDefaults,
  tagsToSeedOptions,
  vehiclesToFormDefaults,
} from '#/widgets/shipment-card'
import { ShipmentRouteMap } from '#/widgets/shipment-route-map'

import { useShipmentSectionSavers } from '../model/use-shipment-section-edit'
import { SidebarCarrierSection } from './sidebar-carrier-section'
import { SidebarCustomerSection } from './sidebar-customer-section'
import { SidebarPaymentSection } from './sidebar-payment-section'

interface ShipmentSidebarDetailsProps {
  shipment?: ShipmentDetail
  isLoading?: boolean
  stage?: ShipmentStage
}

function DetailsSkeleton() {
  return (
    <div className="flex flex-col gap-6 p-2.5">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex flex-col gap-2 px-2.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-3/4" />
        </div>
      ))}
    </div>
  )
}

function useSectionEditor<T>(onSave: (data: T) => Promise<void>) {
  const [editing, setEditing] = useState(false)
  const save = async (data: T) => {
    try {
      await onSave(data)
      setEditing(false)
    } catch {
      // keep the editor open so the user can correct and retry
    }
  }
  return {
    editing,
    start: () => setEditing(true),
    cancel: () => setEditing(false),
    save,
  }
}

function useSidebarDefaults(shipment: ShipmentDetail) {
  const vehicles = useMemo(() => vehiclesToFormDefaults(shipment), [shipment])
  const location = useMemo(() => locationToFormDefaults(shipment), [shipment])
  const fields = useMemo(() => shipmentToFormDefaults(shipment), [shipment])
  const tags = useMemo(() => tagsToSeedOptions(shipment), [shipment])
  return { vehicles, location, fields, tags }
}

function RouteMapToggle({
  open,
  onToggle,
}: {
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      onPointerEnter={prefetchMapboxMap}
      className="cursor-pointer underline select-none hover:text-gray-900"
    >
      {t(open ? 'detailSidebar.hideRoute' : 'detailSidebar.showRoute')}
    </button>
  )
}

function RouteBlock({
  shipment,
  stage,
  onSave,
  onNotesSave,
  defaults,
}: {
  shipment: ShipmentDetail
  stage?: ShipmentStage
  onSave: ReturnType<typeof useShipmentSectionSavers>['onRouteSave']
  onNotesSave: ReturnType<typeof useShipmentSectionSavers>['onNotesSave']
  defaults: ReturnType<typeof useSidebarDefaults>
}) {
  const route = useSectionEditor(onSave)
  const notes = useSectionEditor(onNotesSave)
  const [mapOpen, setMapOpen] = useState(false)
  return (
    <div className="flex flex-col gap-1">
      <RouteSection
        stops={shipment.stops ?? []}
        trailer={shipment.transport_type}
        estimatedPickupAt={shipment.estimated_pickup_at}
        estimatedDeliveryAt={shipment.estimated_delivery_at}
        firstAvailableDate={shipment.first_available_date}
        editing={route.editing}
        onEditStart={route.start}
        onCancel={route.cancel}
        onSave={route.save}
        locationDefaults={defaults.location}
        shipmentDefaults={defaults.fields}
        tagSeedOptions={defaults.tags}
        customer={shipment.customer}
        hideNotes
        editClassName="rounded-lg"
        flush
        showEditTitle
        // Location type only applies to orders; lead/quote use the compact
        // route (matches the full detail page's compactRoute={!isOrder}).
        compact={stage !== SHIPMENT_STAGES.ORDER}
        headerExtra={
          <RouteMapToggle
            open={mapOpen}
            onToggle={() => setMapOpen((prev) => !prev)}
          />
        }
      />
      <NotesSection
        lbNote={shipment.loadboard_instructions ?? ''}
        cmNote={shipment.instructions ?? ''}
        editing={notes.editing}
        onEditStart={notes.start}
        onCancel={notes.cancel}
        onSave={notes.save}
        editClassName="bg-gray-100 rounded-lg"
        flush
        showEditTitle
      />
      {mapOpen && (
        <div className="pt-1.5">
          <ShipmentRouteMap
            stops={shipment.stops}
            status={shipment.status}
            className="h-80"
          />
        </div>
      )}
    </div>
  )
}

function DetailsContent({
  shipment,
  stage,
}: {
  shipment: ShipmentDetail
  stage?: ShipmentStage
}) {
  const { close } = useDetailSidebar()
  const savers = useShipmentSectionSavers(shipment, { onConverted: close })
  const defaults = useSidebarDefaults(shipment)
  const vehicle = useSectionEditor(savers.onVehiclesSave)
  const priceMode = stage === SHIPMENT_STAGES.LEAD

  return (
    <div className="flex flex-col gap-6 p-2.5">
      {/* No edit-form-fields wrapper: the vehicle editor sits on a gray-100
          form, so its inputs keep their default white bg to stand out. */}
      <VehicleSection
        vehicles={shipment.vehicles ?? []}
        editing={vehicle.editing}
        onEditStart={vehicle.start}
        onCancel={vehicle.cancel}
        onSave={vehicle.save}
        defaultValues={defaults.vehicles}
        flush
      />
      <RouteBlock
        shipment={shipment}
        stage={stage}
        onSave={savers.onRouteSave}
        onNotesSave={savers.onNotesSave}
        defaults={defaults}
      />
      <SidebarCustomerSection shipment={shipment} />
      <SidebarPaymentSection
        shipment={shipment}
        priceMode={priceMode}
        onSave={priceMode ? savers.onConvertToQuote : savers.onPaymentSave}
      />
      {stage === SHIPMENT_STAGES.ORDER && (
        <SidebarCarrierSection shipment={shipment} />
      )}
      {stage === SHIPMENT_STAGES.LEAD && <LeadRouteMap shipment={shipment} />}
    </div>
  )
}

function LeadRouteMap({ shipment }: { shipment: ShipmentDetail }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="-mx-2.5 border-t border-gray-200" />
      <ShipmentRouteMap
        stops={shipment.stops}
        status={shipment.status}
        className="h-80"
      />
    </div>
  )
}

export function ShipmentSidebarDetails({
  shipment,
  isLoading = false,
  stage,
}: ShipmentSidebarDetailsProps) {
  if (isLoading || !shipment) return <DetailsSkeleton />
  return <DetailsContent shipment={shipment} stage={stage} />
}
